'use client';

import { useState, useEffect, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);

  return matches;
}

export function LeaderboardTable({
  data,
  onCompare,
  isSingleSubject,
  currentUser,
  allSubjects,
  onTableScroll,
  topperStudent,
  isSingleSectionMultipleSubjects,
}) {
  const isMobileOrTablet = useMediaQuery('(max-width: 767px)');
  const tableContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userRowRef = useRef(null);
  const [isUserRowInView, setIsUserRowInView] = useState(true);
  const [isTableTall, setIsTableTall] = useState(false);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const handleScroll = (e) => {
      if (onTableScroll) {
        onTableScroll(e.target.scrollLeft);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [onTableScroll]);

  useEffect(() => {
    window.mainTableScrollTo = (scrollLeft) => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = scrollLeft;
      }
    };
  }, []);

  useEffect(() => {
    const tableEl = tableContainerRef.current;
    const userRowEl = userRowRef.current;

    if (tableEl && tableEl.scrollHeight > window.innerHeight * 0.95) {
      setIsTableTall(true);
    } else {
      setIsTableTall(false);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsUserRowInView(entry.isIntersecting);
      },
      { threshold: 0.1 },
    );

    if (userRowEl) {
      observer.observe(userRowEl);
    }

    return () => {
      if (userRowEl) {
        observer.unobserve(userRowEl);
      }
    };
  }, [data, currentUser]);

  const subjectKeys =
    !isSingleSubject && allSubjects && allSubjects.length > 0
      ? allSubjects.map((subject) => ({
          key: subject.subjectId.shortName
            ? subject.subjectId.shortName.toLowerCase()
            : subject.subjectId.title.toLowerCase(),
          display: subject.subjectId.shortName || subject.subjectId.title,
        }))
      : [];

  const getAvatarBackground = (rank) => {
    if (!isMobileOrTablet) {
      return 'var(--q3-surface-dimmer)';
    }
    switch (rank) {
      case 1:
        return 'linear-gradient(180deg, #F54A00 0%, #E17100 100%)';
      case 2:
        return 'linear-gradient(180deg, #A9B5C3 0%, #636F7D 100%)';
      case 3:
        return 'linear-gradient(180deg, #A93410 0%, #CB3E13 100%)';
      default:
        return 'var(--q3-surface-dimmer)';
    }
  };

  const tableData = isMobileOrTablet ? data : data.filter((student) => student.rank > 3);
  const rankWidth = isMobileOrTablet ? 60 : 100;
  const nameWidth = isMobileOrTablet ? 140 : 280;
  const scoreWidth = isMobileOrTablet ? 140 : 180;
  const subjectWidth = subjectKeys.length * (isMobileOrTablet ? 70 : 120);
  const accuracyWidth = isMobileOrTablet ? 80 : 100;
  const compareWidth = isMobileOrTablet ? 140 : 160;
  let minTableWidth = rankWidth + nameWidth + scoreWidth + accuracyWidth + compareWidth;
  if (!isSingleSectionMultipleSubjects && !isSingleSubject) {
    minTableWidth += subjectWidth;
  }

  return (
    <div className="relative">
      {/* Main table container with horizontal scroll */}
      <div ref={scrollContainerRef} className="overflow-x-auto rounded-lg border">
        <div style={{ minWidth: `${minTableWidth}px` }}>
          {/* Sticky Header - positioned relative to viewport but contained within horizontal scroll */}
          <div className="sticky top-0 z-30 bg-[var(--q3-surface-dim)]">
            <Table
              className="table-fixed"
              style={{
                tableLayout: 'fixed',
                // width: `${minTableWidth}px`,
              }}
            >
              <TableHeader className="h-16">
                <TableRow style={{ backgroundColor: 'var(--q3-surface-dim)' }}>
                  <TableHead
                    className="font-semibold rounded-tl-lg"
                    style={{
                      width: `${rankWidth}px`,
                      maxWidth: `${rankWidth}px`,
                      minWidth: `${rankWidth}px`,
                    }}
                  >
                    Rank
                  </TableHead>
                  <TableHead
                    className="font-semibold"
                    style={{
                      width: `${nameWidth}px`,
                      maxWidth: `${nameWidth}px`,
                      minWidth: `${nameWidth}px`,
                    }}
                  >
                    Student Name
                  </TableHead>
                  <TableHead
                    className="font-semibold text-center"
                    style={{
                      width: `${scoreWidth}px`,
                      maxWidth: `${scoreWidth}px`,
                      minWidth: `${scoreWidth}px`,
                    }}
                  >
                    {isSingleSubject ? 'Score' : 'Overall Score'}
                  </TableHead>
                  {!isSingleSubject &&
                    !isSingleSectionMultipleSubjects &&
                    subjectKeys.map((subject) => (
                      <TableHead
                        key={subject.key}
                        className="w-[70px] md:w-[120px] font-semibold text-center capitalize truncate"
                        style={{ color: 'var(--q3-neutral-default)' }}
                      >
                        {subject.display}
                      </TableHead>
                    ))}
                  <TableHead
                    className="font-semibold text-center"
                    style={{
                      color: 'var(--q3-neutral-default)',
                      width: `${accuracyWidth}px`,
                      maxWidth: `${accuracyWidth}px`,
                      minWidth: `${accuracyWidth}px`,
                    }}
                  >
                    Accuracy
                  </TableHead>
                  <TableHead
                    className="font-semibold text-center rounded-tr-lg"
                    style={{
                      width: `${compareWidth}px`,
                      maxWidth: `${compareWidth}px`,
                      minWidth: `${compareWidth}px`,
                    }}
                  >
                    Compare
                  </TableHead>
                </TableRow>
              </TableHeader>
            </Table>
          </div>

          {/* Scrollable Table Body */}
          <div ref={tableContainerRef} className="overflow-y-auto h-[60vh] md:h-[70vh]">
            <Table
              className="table-fixed"
              style={{
                tableLayout: 'fixed',
                // width: `${minTableWidth}px`,
              }}
            >
              <TableBody>
                {tableData &&
                  tableData.map((student, index) => (
                    <TableRow
                      onClick={() =>
                        !student.isCurrentUser && topperStudent.rank !== student.rank && onCompare(student)
                      }
                      key={index}
                      ref={student.isCurrentUser ? userRowRef : null}
                      className="h-16 cursor-pointer hover:bg-[var(--q3-surface-dim)]/50"
                      style={student.isCurrentUser ? { backgroundColor: 'var(--q3-blue-lightest)' } : {}}
                    >
                      <TableCell
                        className="font-medium"
                        style={{
                          color: 'var(--q3-neutral-default)',
                          width: `${rankWidth}px`,
                          maxWidth: `${rankWidth}px`,
                          minWidth: `${rankWidth}px`,
                        }}
                      >
                        <Badge
                          style={{ background: getAvatarBackground(student.rank) }}
                          className="rounded-full bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-default)] border-[var(--q3-stroke-normal)] w-10 h-10"
                        >
                          {student.rank}
                        </Badge>
                      </TableCell>
                      <TableCell
                        style={{
                          width: `${nameWidth}px`,
                          maxWidth: `${nameWidth}px`,
                          minWidth: `${nameWidth}px`,
                        }}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Avatar className="h-8 w-8 hidden md:block flex-shrink-0">
                            <AvatarImage src={student?.avatar || '/placeholder.svg'} />
                            <AvatarFallback>
                              {student.name &&
                                student?.name
                                  ?.split(' ')
                                  .map((n) => n[0])
                                  .join('')}
                            </AvatarFallback>
                          </Avatar>

                          <div className="overflow-hidden">
                            <span className="font-medium inline md:hidden truncate">
                              {student?.name && student?.name?.length > 16
                                ? student?.name?.substring(0, 10) + '...'
                                : student?.name}{' '}
                              {student?.isCurrentUser && ' (You)'}
                            </span>
                            <span className="font-medium hidden md:inline truncate">
                              {student.name && student.name} {student.isCurrentUser && ' (You)'}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        className="text-center"
                        style={{
                          width: `${scoreWidth}px`,
                          maxWidth: `${scoreWidth}px`,
                          minWidth: `${scoreWidth}px`,
                        }}
                      >
                        <div
                          className="inline-flex items-center justify-center border-1 rounded-full bg-[var(--q3-surface-dim)] px-3 py-1"
                          style={{ color: 'var(--q3-neutral-default)' }}
                        >
                          <span className="font-semibold text-base text-[var(--q3-neutral-default)]">
                            {Number.isInteger(student.overallScore)
                              ? student.overallScore
                              : student.overallScore?.toFixed(2)}
                          </span>
                          <span className="text-xs text-[var(--q3-neutral-light)]">/{student.maxScore}</span>
                        </div>
                      </TableCell>
                      {!isSingleSubject &&
                        !isSingleSectionMultipleSubjects &&
                        subjectKeys.map((subject) => (
                          <TableCell
                            key={subject.key}
                            className="text-center font-medium"
                            style={{
                              color: 'var(--q3-neutral-default)',
                              width: `${isMobileOrTablet ? 60 : 120}px`,
                              maxWidth: `${isMobileOrTablet ? 60 : 120}px`,
                              minWidth: `${isMobileOrTablet ? 60 : 120}px`,
                            }}
                          >
                            {student[subject.key] !== undefined ? student[subject.key] : '--'}
                          </TableCell>
                        ))}
                      <TableCell
                        className="text-center font-medium"
                        style={{
                          color: 'var(--q3-neutral-default)',
                          width: `${accuracyWidth}px`,
                          maxWidth: `${accuracyWidth}px`,
                          minWidth: `${accuracyWidth}px`,
                        }}
                      >
                        {Number(student.accuracy)?.toFixed(2) || student.accuracy}%
                      </TableCell>
                      <TableCell
                        className="text-center"
                        style={{
                          width: `${compareWidth}px`,
                          maxWidth: `${compareWidth}px`,
                          minWidth: `${compareWidth}px`,
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCompare(student)}
                          className="text-[var(--q3-accent-normal)] hover:text-[var(--q3-accent-normal)]/90 hover:bg-[var(--q3-surface-dim)] hover:scale-[1.02] rounded-full border-1"
                          disabled={student.isCurrentUser}
                        >
                          Compare
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
